import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

const EMPTY = { customerId: '', title: '', startDate: '', endDate: '', notes: '' };

export function ContractCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const customers = useQuery({ queryKey: ['sales', 'customers'], queryFn: () => salesApi.listCustomers({ limit: 200 }).then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(EMPTY); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => salesApi.createContract(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['sales', 'contracts'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.customerId || !form.title.trim()) { setError(t('sc.error.required')); return; }
    save.mutate({ customerId: form.customerId, title: form.title.trim(), startDate: form.startDate || null, endDate: form.endDate || null, notes: form.notes || null });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('sc.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('sc.field.customer')}</span>
          <select className="input" value={form.customerId} onChange={set('customerId')} required>
            <option value="">-</option>
            {(customers.data || []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('sc.field.title')}</span>
          <input className="input" value={form.title} onChange={set('title')} required />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('sc.field.start')}</span>
            <input className="input" type="date" value={form.startDate} onChange={set('startDate')} />
          </label>
          <label className="field">
            <span className="field-label">{t('sc.field.end')}</span>
            <input className="input" type="date" value={form.endDate} onChange={set('endDate')} />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
